import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EecModalService } from '~app/modules/product/services/eec-modal.service';
import { EnergyLabelService } from '../../services/energy-label.service';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EecLabels } from '~shared/models/product-card/eec-labels.model';

@Component({
  selector: 'app-search-product-energy-efficiency-new',
  templateUrl: './product-energy-efficiency-new.component.html',
  styleUrls: ['./product-energy-efficiency-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductEnergyEfficiencyNewComponent implements OnInit, OnDestroy {
  @Input() energyEfficiencyClass?: string;
  @Input() energyEfficiencyRange?: { from: string; to: string };
  @Input() energyEfficiencyImage: string;
  //type is any because we receive dynamic fields
  @Input() energyConfig: any;
  @Input() productId?: string;
  @Input() productSku?: string;
  @Input() eecLabel?: EecLabels;

  energyLabelSrc: string;
  isGenericLabel = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly energyEfficiencyModalService: EecModalService,
    private readonly energyLabelService: EnergyLabelService,
    private readonly serverLogger: ServerLoggerService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const defaultAttribute: string = this.energyConfig?.energy_efficiency_label_config?.defaultAttribute ?? '';

    this.energyEfficiencyClass =
      this.eecLabel?.class || this.energyEfficiencyClass || this.energyConfig?.[defaultAttribute];

    const possibleLabels = this.energyConfig?.energy_efficiency_label_config?.values;
    if (!this.eecLabel?.labelUrl && possibleLabels && !possibleLabels.includes(this.energyEfficiencyClass)) {
      this.isGenericLabel = true;

      this.serverLogger.error('Energy label - Generic image used due to configuration issue', {
        component: 'ProductEnergyEfficiencyNewComponent',
        productId: this.productId,
      });

      this.setGenericImageSrc();
    } else {
      this.setEnergyLabelSrc();
    }
  }

  private setGenericImageSrc(): void {
    this.energyLabelService
      .isNewEnergyLabelEnabled()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isNewEnergyLabelEnabled) => {
        this.energyLabelSrc = this.energyLabelService.getGenericImageUrl(isNewEnergyLabelEnabled);
        this.cdr.detectChanges();
      });
  }

  private setEnergyLabelSrc(): void {
    this.energyLabelService
      .isNewEnergyLabelEnabled()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isNewEnergyLabelEnabled) => {
        const max =
          this.eecLabel?.classRange?.max ||
          this.eecLabel?.classRange?.from ||
          this.energyEfficiencyRange?.from ||
          this.energyConfig?.energy_efficiency_label_config?.range?.max;
        const min =
          this.eecLabel?.classRange?.min ||
          this.eecLabel?.classRange?.to ||
          this.energyEfficiencyRange?.to ||
          this.energyConfig?.energy_efficiency_label_config?.range?.min;

        this.energyLabelSrc = this.energyLabelService.getEnergyLabelUrl(
          isNewEnergyLabelEnabled,
          max,
          min,
          this.energyEfficiencyClass
        );
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEnergyEfficiencyLabelClick(event): void {
    if (!this.energyEfficiencyImage) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    this.energyEfficiencyModalService.openEecDocument(this.energyEfficiencyImage);
  }
}
